import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AnalyseIA from './AnalyseIA';
import { lignesDuBloc, totauxDuBloc, blocsAnalysables } from '../utils/lignesDuDashboard';
import { AlertCircle, EyeOff, Users, TrendingUp } from 'lucide-react';
import PianoPerformanceChart from './PianoPerformanceChart';
import DashboardLoadingScreen from './DashboardLoadingScreen';
import HiddenModulesModal from './HiddenModulesModal';
import { useClientModuleVisibility } from '../hooks/useClientModuleVisibility';
import pianoLogo from '../logos/piano-analytics.png';
import { dateLocale, jourIso } from '../utils/periodUtils';

/**
 * Agrège les données quotidiennes selon la granularité
 * (réplique de la logique backend pour éviter les rechargements)
 */
function aggregateByGranularity(dailyData, granularity) {
  if (!dailyData || dailyData.length === 0) return [];

  if (granularity === 'day') {
    return dailyData;
  }

  // Pour les autres granularités, grouper par période
  const grouped = {};

  dailyData.forEach(day => {
    const periodKey = getPeriodKey(day.date, granularity);

    if (!grouped[periodKey]) {
      grouped[periodKey] = {
        date: periodKey,
        visits: 0,
        visitors: 0,
        pageViews: 0,
        conversions: 0,
        conversionsValue: 0,
        bounceSum: 0,
        timeSum: 0,
        dayCount: 0
      };
    }

    grouped[periodKey].visits += day.visits || 0;
    grouped[periodKey].visitors += day.visitors || 0;
    grouped[periodKey].pageViews += day.pageViews || 0;
    grouped[periodKey].conversions += day.conversions || 0;
    grouped[periodKey].conversionsValue += day.conversionsValue || 0;
    grouped[periodKey].bounceSum += (day.bounceRate || 0) * (day.visits || 0);
    grouped[periodKey].timeSum += (day.avgTimeOnSite || 0) * (day.visits || 0);
    grouped[periodKey].dayCount++;
  });

  // Calculer les moyennes
  return Object.values(grouped).map(period => ({
    date: period.date,
    visits: period.visits,
    visitors: period.visitors,
    pageViews: period.pageViews,
    conversions: period.conversions,
    conversionsValue: period.conversionsValue,
    bounceRate: period.visits > 0
      ? (period.bounceSum / period.visits)
      : 0,
    avgTimeOnSite: period.visits > 0
      ? (period.timeSum / period.visits)
      : 0
  })).sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Retourne la clé de période pour une date
 */
function getPeriodKey(dateStr, granularity) {
  // Minuit local, et non minuit UTC : voir `dateLocale`. Le repère doit être le même à la
  // lecture et à l'écriture, sans quoi le lundi d'une semaine saute d'un jour.
  const date = dateLocale(dateStr);

  switch (granularity) {
    case 'week':
      // Trouver le lundi de la semaine
      const day = date.getDay();
      const diff = date.getDate() - day + (day === 0 ? -6 : 1);
      const monday = new Date(date);
      monday.setDate(diff);
      return jourIso(monday);

    case 'month':
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

    case 'quarter':
      const quarter = Math.floor(date.getMonth() / 3) + 1;
      return `${date.getFullYear()}-Q${quarter}`;

    case 'year':
      return `${date.getFullYear()}`;

    default:
      return dateStr;
  }
}

/**
 * PianoDashboardView - Vue dashboard pour Piano Analytics
 *
 * Affiche:
 * - Modal de sélection de site (si aucun site sélectionné)
 * - Composant Résultats (PianoPerformanceChart) - style identique à Matomo/Google Ads
 */

const PianoDashboardView = ({
  /**
   * L'analyse IA est-elle ouverte à cet utilisateur ?
   *
   * Un membre d'organisation est jugé sur son plan, un accès client sur la permission
   * `analyse-ia` accordée dans Organisation › Accès clients. La règle est calculée une fois
   * dans `App` et descendue ici : `autorise` valant `true` par défaut, ne rien passer montrait
   * le bouton à un client que le serveur refuse ensuite.
   */
  analyseAutorisee = false,
  // Notes à reporter sur le graphique. Traversent la vue sans y être lues.
  dashboardNotes = [],
  token,
  selectedPeriod,
  customStartDate,
  customEndDate,
  pianoSites,
  filteredPianoSites = [],
  setPianoSites,
  pianoSelectedSiteId,
  setPianoSelectedSiteId,
  pianoDashboardData,
  setPianoDashboardData,
  pianoLoading,
  setPianoLoading,
  pianoGranularity,
  setPianoGranularity,
  KPICard,
  formatNumber,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  // Props pour la comparaison
  compareEnabled = false,
  comparisonType = 'previous_period'
}) => {
  const [error, setError] = useState(null);
  const [sitesLoading, setSitesLoading] = useState(false);

  // Calculer le clientId effectif pour la visibilité des modules
  const effectiveClientId = useMemo(() => {
    return pianoSelectedSiteId ? `piano_${pianoSelectedSiteId}` : null;
  }, [pianoSelectedSiteId]);

  // Hook pour la visibilité des modules par client
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  // State pour le modal des modules masqués
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Charger la liste des sites Piano au montage (seulement si pas déjà chargés)
  useEffect(() => {
    const fetchPianoSites = async () => {
      if (!token) return;
      // Ne pas recharger si on a déjà des sites
      if (pianoSites && pianoSites.length > 0) {
        return;
      }

      setSitesLoading(true);
      try {
        const response = await fetch('/api/dashboard/piano/sites', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();

        if (data.success && data.sites) {
          setPianoSites(data.sites);
        } else {
          setError(data.error || 'Erreur lors du chargement des sites Piano Analytics');
        }
      } catch (err) {
        console.error('Erreur fetch sites Piano:', err);
        setError('Impossible de charger les sites Piano Analytics');
      } finally {
        setSitesLoading(false);
      }
    };

    fetchPianoSites();
  }, [token, pianoSites, setPianoSites]);

  // Auto-sélectionner si un seul site Piano filtré disponible
  useEffect(() => {
    if (!sitesLoading && filteredPianoSites.length === 1 && !pianoSelectedSiteId) {
      console.log('[PIANO] Auto-sélection du seul site:', filteredPianoSites[0].name);
      setPianoSelectedSiteId(filteredPianoSites[0].id);
    }
  }, [sitesLoading, filteredPianoSites, pianoSelectedSiteId, setPianoSelectedSiteId]);

  // Charger les données du dashboard quand le site ou la période change
  const fetchDashboardData = useCallback(async () => {
    if (!token || !pianoSelectedSiteId) return;

    setPianoLoading(true);
    setError(null);

    try {
      const response = await fetch('/api/dashboard/piano/timeline', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          siteId: pianoSelectedSiteId,
          period: selectedPeriod,
          granularity: 'day',
          customStartDate,
          customEndDate,
          compareEnabled,
          comparisonType
        })
      });

      const data = await response.json();

      if (data.success) {
        setPianoDashboardData(data);
      } else {
        setError(data.error || 'Erreur lors du chargement des données');
        setPianoDashboardData(null);
      }
    } catch (err) {
      console.error('Erreur fetch dashboard Piano:', err);
      setError('Impossible de charger les données du dashboard');
      setPianoDashboardData(null);
    } finally {
      setPianoLoading(false);
    }
  }, [token, pianoSelectedSiteId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, setPianoLoading, setPianoDashboardData]);

  // Charger les données quand les paramètres changent
  useEffect(() => {
    if (pianoSelectedSiteId) {
      fetchDashboardData();
    }
  }, [pianoSelectedSiteId, selectedPeriod, customStartDate, customEndDate, compareEnabled, comparisonType, fetchDashboardData]);

  // Agréger les données quotidiennes selon la granularité sélectionnée
  const aggregatedDailyData = useMemo(() => {
    const rawDaily = pianoDashboardData?.data?.daily || [];
    return aggregateByGranularity(rawDaily, pianoGranularity);
  }, [pianoDashboardData?.data?.daily, pianoGranularity]);

  // Handler pour le changement de site
  const handleSiteChange = (e) => {
    const newSiteId = e.target.value;
    if (newSiteId) {
      setPianoSelectedSiteId(newSiteId);
      setPianoDashboardData(null);
    }
  };

  // Handler pour le changement de granularité
  const handleGranularityChange = (newGranularity) => {
    setPianoGranularity(newGranularity);
  };

  // Grouper les sites filtrés par connexion
  const sitesByConnection = filteredPianoSites.reduce((acc, site) => {
    const connectionKey = site.connectionName || 'Piano Analytics';
    if (!acc[connectionKey]) {
      acc[connectionKey] = [];
    }
    acc[connectionKey].push(site);
    return acc;
  }, {});

  // Cas 1: Chargement en cours
  if (sitesLoading || pianoLoading) {
    return (
      <DashboardLoadingScreen
        platformName="Piano Analytics"
        features={[
          { icon: <Users size={28} />, label: 'Visiteurs et sessions' },
          { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
          { icon: <img src={pianoLogo} alt="Piano Analytics" style={{ width: '28px', height: '28px', borderRadius: '6px' }} />, label: 'Analyse du trafic' }
        ]}
      />
    );
  }

  // Cas 2: Aucun site Piano configuré
  if (pianoSites.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <img src={pianoLogo} alt="Piano Analytics" style={{ width: '64px', height: '64px', borderRadius: '12px' }} />
          </div>
          <h2>Aucun site Piano Analytics configuré</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Connectez Piano Analytics dans "Mes Connecteurs" pour commencer.
          </p>
          <button
            className="btn btn-primary"
            onClick={() => window.dispatchEvent(new CustomEvent('navigateTo', { detail: { view: 'connectors' } }))}
            style={{ fontSize: '16px', padding: '12px 24px' }}
          >
            + Ajouter un connecteur
          </button>
        </div>
      </div>
    );
  }

  // Cas 2b: Des sites existent mais aucun ne correspond au filtre client/site
  if (filteredPianoSites.length === 0) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <span style={{ fontSize: '48px' }}>🔍</span>
          </div>
          <h2>Aucun site Piano Analytics associé</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Aucun site Piano Analytics n'est associé au client ou site sélectionné.<br />
            Associez des comptes via "Gestion des sites" pour les voir apparaître ici.
          </p>
        </div>
      </div>
    );
  }

  // Cas 3: Sites disponibles mais aucun sélectionné
  if (!pianoSelectedSiteId || !filteredPianoSites.find(s => s.id === pianoSelectedSiteId)) {
    return (
      <div className="account-selection-prompt">
        <div className="account-selection-card">
          <div className="account-selection-icon">
            <img src={pianoLogo} alt="Piano Analytics" style={{ width: '64px', height: '64px', borderRadius: '12px' }} />
          </div>
          <h2>Sélectionnez un site Piano Analytics</h2>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            {filteredPianoSites.length} site{filteredPianoSites.length > 1 ? 's' : ''} disponible{filteredPianoSites.length > 1 ? 's' : ''}
          </p>
          <select
            className="account-selection-dropdown"
            value=""
            onChange={handleSiteChange}
            style={{
              width: '100%',
              padding: '12px 16px',
              fontSize: '16px',
              borderRadius: '8px',
              border: '1px solid #d1d5db',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="">Choisir un site...</option>
            {Object.keys(sitesByConnection).length > 1 ? (
              Object.entries(sitesByConnection).map(([connectionName, sites]) => (
                <optgroup key={connectionName} label={connectionName}>
                  {sites.sort((a, b) => a.name.localeCompare(b.name)).map(site => (
                    <option key={site.id} value={site.id}>
                      {site.name}
                    </option>
                  ))}
                </optgroup>
              ))
            ) : (
              [...filteredPianoSites].sort((a, b) => a.name.localeCompare(b.name)).map(site => (
                <option key={site.id} value={site.id}>
                  {site.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>
    );
  }

  // Cas 4: Site sélectionné mais aucune donnée
  const summary = pianoDashboardData?.data?.summary;
  const hasData = summary && (
    (summary.totalVisits || summary.visits || 0) > 0 ||
    (summary.uniqueVisitors || summary.visitors || 0) > 0 ||
    (summary.totalPageviews || summary.pageViews || 0) > 0
  );

  if (!hasData && pianoDashboardData && !pianoLoading) {
    return (
      <div className="piano-dashboard-view">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '60px 24px',
          backgroundColor: '#FAF9F5',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <img
            src={pianoLogo}
            alt="Piano Analytics"
            style={{ width: '48px', height: '48px', borderRadius: '8px', opacity: 0.5, marginBottom: '16px' }}
          />
          <h3 style={{ color: '#64748b', fontWeight: '500', marginBottom: '8px' }}>
            Aucune donnée disponible pour cette période
          </h3>
          <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
            Essayez de sélectionner une période différente ou vérifiez que le tracking Piano Analytics est actif.
          </p>
        </div>
      </div>
    );
  }

  // Cas 5: Site sélectionné avec données - Afficher le dashboard
  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    /*
     * **Les blocs vivent sous `data`, et les parcourir un cran trop haut n'en rendait qu'un.**
     *
     * `pianoDashboardData` vaut `{ success, data: { summary, daily, channels, geography, devices, … } }`.
     * En bouclant sur le premier niveau, la seule entrée exploitable était `data` tout entier, et
     * `lignesDuBloc` — qui ne rend qu'**un** tableau, le plus grand — y choisissait la
     * géographie. L'analyse ne voyait donc que les continents et concluait, à juste titre, qu'il
     * n'y avait rien d'autre à dire.
     *
     * Le repli sur l'objet lui-même garde l'écran GA4 en état : là, les blocs sont au premier
     * niveau.
     */
    const sourceDonnees = blocsAnalysables(pianoDashboardData);
    const blocs = {};
    const absents = [];
    // Le détail de cet écran vit sous des clés qui diffèrent d'une version à l'autre : on
    // parcourt ce qu'il y a, plutôt que d'en nommer une liste qui vieillirait mal.
    for (const [libelle, bloc] of Object.entries(sourceDonnees)) {
      if (!bloc || typeof bloc !== 'object') continue;
      const reduit = lignesDuBloc(bloc);
      if (reduit) { blocs[libelle] = reduit; continue; }
      // Un bloc sans lignes porte souvent les chiffres de tête — `currentPeriod` et consorts.
      const totaux = totauxDuBloc(bloc);
      if (totaux) blocs[libelle] = { totaux };
      else absents.push(libelle);
    }

    const site = (filteredPianoSites || []).find((s) => String(s.id ?? s.idsite ?? s.idSite) === String(pianoSelectedSiteId));

    return {
      dashboardData: {
        sources: [pianoSelectedSiteId].filter(Boolean),
        ...periode,
        charts: sourceDonnees.charts,
        composants: { blocs, absents },
        dashboardType: 'piano'
      },
      dashboardType: 'piano',
      accountId: pianoSelectedSiteId || null,
      accountName: site?.name || site?.displayName || null,
      ...periode
    };
  };

  return (
    <div className="piano-dashboard-view">
      {/*
        L'analyse IA. Ce dashboard n'a pas de barre de filtres : le bouton occupe sa propre
        ligne, alignée à droite comme les autres actions d'écran. Le composant rend aussi le
        panneau, qui passe par un portail — sa place dans l'arbre n'a aucune incidence.
      */}
      {pianoSelectedSiteId && !pianoLoading && pianoDashboardData && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            autorise={analyseAutorisee}
            compte={{ id: pianoSelectedSiteId }}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {/* Barre avec lien Modules masqués */}
      {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '16px'
        }}>
          <div
            className="hidden-modules-link"
            onClick={() => setIsHiddenModulesModalOpen(true)}
            title="Voir les modules masqués pour le client"
          >
            <EyeOff size={16} />
            <span>Modules masqués</span>
            <span className="hidden-count">{clientHiddenModules.length}</span>
          </div>
        </div>
      )}

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Message d'erreur */}
      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '12px 16px',
          backgroundColor: '#fef2f2',
          border: '1px solid #fecaca',
          borderRadius: '8px',
          marginBottom: '16px',
          color: '#b91c1c'
        }}>
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {/* Graphique de performance Piano - même style que Matomo/Google Ads */}
      {(userRole !== 'CLIENT' || isClientModuleVisible('piano_performance')) && (
        <PianoPerformanceChart
          notes={dashboardNotes}
          data={aggregatedDailyData}
          summary={pianoDashboardData?.data?.summary}
          previousPeriodData={pianoDashboardData?.previousPeriod}
          channels={pianoDashboardData?.data?.channels || []}
          geography={pianoDashboardData?.data?.geography || []}
          devices={pianoDashboardData?.data?.devices || []}
          browsers={pianoDashboardData?.data?.browsers || []}
          languages={pianoDashboardData?.data?.languages || []}
          pages={pianoDashboardData?.data?.pages || []}
          weekdays={pianoDashboardData?.data?.weekdays || []}
          ecommerce={pianoDashboardData?.data?.ecommerce}
          granularity={pianoGranularity}
          onGranularityChange={handleGranularityChange}
          loading={pianoLoading}
          KPICard={KPICard}
          formatNumber={formatNumber}
          siteId={pianoSelectedSiteId}
          token={token}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          accountId={accountId}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="piano_performance"
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
        />
      )}
    </div>
  );
};

export default PianoDashboardView;
